import {createInstance, Identify, Types} from '@amplitude/analytics-browser';
import {ROUTE_PATHS} from 'constant';
import {isProd} from 'helpers/environment';

import {AnalyticsKeys} from './keys';
import {getNavigationContext, NavigationContext} from './navigationInfo';
import {getAnonymizedRoute} from './routeInfo';
const amplitudeApiKey = isProd
  ? 'd4e327b971d003107da9982d349da66b'
  : '3f39cbd848a6e54267723a7be5f915fd';

type EventData = {[key: string]: boolean | number | string | undefined | string[]};

type AuthRedirectTarget = 'login' | 'waitlist';
type AuthRedirect = {key: string; timestamp: number};
const AUTH_REDIRECT_DEDUPLICATION_WINDOW_MS = 1000;
let lastAuthRedirect: AuthRedirect | undefined;

// Enrichment plugin that replaces raw pathnames with anonymized route patterns
const routeAnonymizationPlugin = () => ({
  name: 'route-anonymization',
  type: 'enrichment' as const,
  setup: async () => undefined,
  execute: async (event: Types.Event) => {
    const props = event.event_properties as Record<string, unknown> | undefined;
    if (!props) return event;

    const rawPath = props['[Amplitude] Page Path'];
    if (typeof rawPath !== 'string') return event;

    const pattern = getAnonymizedRoute(rawPath);

    props['[Amplitude] Page Path'] = pattern;

    const pageUrl = props['[Amplitude] Page URL'];
    if (typeof pageUrl === 'string') {
      props['[Amplitude] Page URL'] = anonymizePageLocation(pageUrl, pattern);
    }

    const pageLoc = props['[Amplitude] Page Location'];
    if (typeof pageLoc === 'string') {
      props['[Amplitude] Page Location'] = anonymizePageLocation(pageLoc, pattern);
    }

    return event;
  },
});

const anonymizePageLocation = (location: string, pattern: string): string => {
  try {
    const url = new URL(location);
    url.pathname = pattern;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return pattern;
  }
};

class AmplitudeClient {
  isDisabled = false;
  amplitudeInstanceDropbox = createInstance();
  amplitudeInstance = createInstance();

  constructor() {
    this.amplitudeInstance.init(amplitudeApiKey, {
      trackingOptions: {ipAddress: false},
      defaultTracking: {
        sessions: true,
        attribution: false,
        formInteractions: false,
        fileDownloads: false,
        pageViews: {
          trackOn: () => this.shouldTrackPage(),
        },
      },
    });

    const anonymizationPlugin = routeAnonymizationPlugin();
    this.amplitudeInstance.add(anonymizationPlugin);
    this.amplitudeInstanceDropbox.add(anonymizationPlugin);
  }

  private shouldTrackPage() {
    const supportedRoutes = Object.values(ROUTE_PATHS).map(
      (route) => route.split('/:')[0] // strip dynamic segments
    );

    return supportedRoutes.some(
      (path) => window.location.pathname === path || window.location.pathname.startsWith(`${path}/`)
    );
  }

  logEvent(name: AnalyticsKeys, data?: EventData) {
    if (this.isDisabled) return;

    // The route pattern each event fired on, so events shared across pages
    // (e.g. /workstreams and /workstreams-old) can be split by page.
    const props = {app_route: getAnonymizedRoute(), ...data};

    if (!isProd) {
      console.info('Analytics:', name, props);
    }

    this.amplitudeInstanceDropbox.track(name, props);
    this.amplitudeInstance.track(name, props);
  }

  /** Sends queued events with sendBeacon so they still go out while the page unloads. */
  flushOnPageHide() {
    if (this.isDisabled) return;

    this.amplitudeInstanceDropbox.setTransport('beacon');
    this.amplitudeInstance.setTransport('beacon');
    void this.amplitudeInstanceDropbox.flush().promise;
    void this.amplitudeInstance.flush().promise;
  }

  enable(userId: string) {
    this.amplitudeInstanceDropbox.setUserId(userId);
    this.amplitudeInstance.setUserId(userId);
  }

  setProperties(properties: Record<string, boolean | number | string>) {
    if (this.isDisabled) return;

    const keys = Object.keys(properties);
    if (keys.length === 0) return;

    const identifyEvent = new Identify();
    for (const key of keys) {
      identifyEvent.set(key, properties[key]);
    }

    this.amplitudeInstanceDropbox.identify(identifyEvent);
    this.amplitudeInstance.identify(identifyEvent);
  }

  logout() {
    this.amplitudeInstanceDropbox.reset();
    this.amplitudeInstance.reset();
  }

  disable() {
    this.isDisabled = true;
  }

  enableLogging() {
    this.isDisabled = false;
  }
}

let instance: AmplitudeClient | undefined;
export const analyticsLogger = () => {
  if (!instance) {
    instance = new AmplitudeClient();
  }
  return instance;
};

export const logAuthRedirectStarted = (
  returnRoute: string,
  redirectTarget: AuthRedirectTarget,
  navigationContext: NavigationContext = getNavigationContext()
) => {
  const now = Date.now();
  const key = `${returnRoute}:${redirectTarget}`;
  if (
    lastAuthRedirect?.key === key &&
    now - lastAuthRedirect.timestamp < AUTH_REDIRECT_DEDUPLICATION_WINDOW_MS
  ) {
    return;
  }

  lastAuthRedirect = {key, timestamp: now};
  analyticsLogger().logEvent('AUTH_REDIRECT_STARTED', {
    return_route: returnRoute,
    redirect_target: redirectTarget,
    ...navigationContext,
  });
};
